<template>
  <div>
    <hr class="separator" style="margin-top: 1px"/>
    <div class="upgrade-margin">
      <div class="custom-text">
        {{ $t('ParameterManagement.title') }}
      </div>
    </div>
    <div class="all-tab">
      <el-tabs v-model="activeTab" class="parameter-tabs">
        <!-- 电机Tab -->
        <el-tab-pane :label="$t('ParameterManagement.motor')" name="motor">
          <div class="parameter-box">
            <div class="parameter-header">
              <span>{{ $t('ParameterManagement.parameterSettings') }}</span>
            </div>
            <!-- 电机连接状态显示 - 移到电机Tab内部 -->
            <div class="connection-status" style="display: flex; align-items: center; margin: 10px 20px; padding: 10px; background-color: #f5f7fa; border-radius: 4px;">
              <el-tag size="large" :type="motorConnected ? 'success' : 'info'" :loading="motorConnectLoading">
                {{ motorConnected ? $t('ProductUpgrade.online') : $t('ProductUpgrade.offline') }}
              </el-tag>
              <!-- 版本信息显示在在线标签旁边 -->
              <div v-if="motorConnected && motorInfo" style="margin-left: 15px; display: flex; align-items: center;">
                <span v-if="motorInfo.hardwareversion" style="color: #606266; font-size: 14px;">{{ $t('ProductDiagnosis.hardwareVersion') }}: {{ motorInfo.hardwareversion }}</span>
                <span v-if="motorInfo.hardwareversion && motorInfo.softwareversion" style="color: #606266; margin: 0 5px;">/</span>
                <span v-if="motorInfo.softwareversion" style="color: #606266; font-size: 14px;">{{ $t('ProductDiagnosis.firmwareVersion') }}: {{ motorInfo.softwareversion }}</span>
              </div>
              <el-button 
                @click="connectMotor" 
                link 
                type="success" 
                style="margin-left: 10px;">
                {{ $t('ProductUpgrade.reconnecting') }}
              </el-button>
              <div v-if="motorInfo" style="margin-left: 20px;">
                <span v-if="motorInfo.mcusncode" style="margin-right: 15px;">{{ $t('ProductUpgrade.serialNumber') }}: {{ motorInfo.mcusncode }}</span>
              </div>
            </div>
            <div class="parameter-content">
              <!-- 使用v-for遍历参数数组 -->
              <div v-for="(param, index) in parameters" :key="param.key" class="parameter-item">
                <div class="parameter-container">
                  <!-- 限速参数 -->
                  <template v-if="param.key === 'speedLimit'">
                    <template v-if="!isEditing">
                      <div class="parameter-row">
                        <div class="parameter-label">{{ $t('ParameterManagement.speedLimit') }}</div>
                        <div class="parameter-value">
                          <span class="value-text">
                            {{ motorConnected ? param.value : '-' }} {{ $t('ParameterManagement.kmh') }}
                          </span>
                          <el-icon 
                            class="edit-icon" 
                            @click="startEditing" 
                            :disabled="!motorConnected"
                          >
                            <Edit />
                          </el-icon>
                          <span v-if="!motorConnected" class="edit-tip">{{ $t('ParameterManagement.connectMotorFirst') }}</span>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="parameter-row">
                        <div class="parameter-label">{{ $t('ParameterManagement.speedLimit') }}</div>
                        <div class="edit-controls">
                          <span class="range-info">({{ minSpeed }} - {{ maxSpeed }})</span>
                          <div class="input-with-unit">
                            <el-input-number
                              v-model="tempParams.speedLimit"
                              :min="minSpeed"
                              :max="maxSpeed"
                              @change="handleParameterChange('speedLimit', $event)"
                              size="large"
                            ></el-input-number>
                            <span class="unit">{{ $t('ParameterManagement.kmh') }}</span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                  
                  <!-- 只有当不是最后一个参数时才显示分隔线 -->
                  <div v-if="index < parameters.length - 1" class="parameter-divider"></div>
                </div>
              </div>
              
              <!-- 档位最大速度显示 -->
              <div class="gear-speeds-section">
                <div class="gear-speeds-header">
                  <span>{{ $t('ParameterManagement.gearMaxSpeeds') }}</span>
                  <el-button 
                    type="text" 
                    size="small" 
                    class="collapse-btn"
                    @click="gearSpeedsCollapsed = !gearSpeedsCollapsed"
                  >
                    {{ gearSpeedsCollapsed ? $t('ParameterManagement.expand') + ' ▲' : $t('ParameterManagement.collapse') + ' ▼' }}
                  </el-button>
                </div>
                <div v-show="!gearSpeedsCollapsed" class="gear-speeds-content">
                  <div v-for="(gearSpeed, index) in gearMaxSpeeds" :key="gearSpeed.level" class="parameter-item">
                    <div class="parameter-container">
                      <div class="parameter-row">
                        <div class="parameter-label">{{ $t('ParameterManagement.gear') }} {{ gearSpeed.level }}</div>
                        <div v-if="!isEditing" class="parameter-value">
                          <span class="value-text">{{ gearSpeed.speed }} {{ $t('ParameterManagement.kmh') }}</span>
                          <el-icon 
                            class="edit-icon" 
                            @click="startEditing" 
                            :disabled="!motorConnected"
                          >
                            <Edit />
                          </el-icon>
                          <span v-if="!motorConnected" class="edit-tip">{{ $t('ParameterManagement.connectMotorFirst') }}</span>
                        </div>
                        <div v-else class="edit-controls">
                          <div class="input-with-unit">
                            <el-input-number
                              v-model="gearTempSpeeds['gear' + gearSpeed.level]"
                              :min="getGearMinSpeed(gearSpeed.level)"
                              :max="maxSpeed"
                              @change="handleGearSpeedChange(gearSpeed.level, $event)"
                              size="large"
                            ></el-input-number>
                            <span class="unit">{{ $t('ParameterManagement.kmh') }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- 只有当不是最后一个档位时才显示分隔线 -->
                    <div v-if="index < gearMaxSpeeds.length - 1" class="parameter-divider"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 独立的保存按钮区域 -->
            <div class="form-actions" v-if="isEditing">
              <span class="save-btn" @click="saveChanges">{{ $t('ParameterManagement.save') }}</span>
            </div>
          </div>
        </el-tab-pane>
        <!-- 仪表Tab - 暂时隐藏 -->
        <el-tab-pane v-if="false" :label="$t('ParameterManagement.instrument')" name="instrument">
          <div class="parameter-box">
            <div class="parameter-header">
              <span>{{ $t('ParameterManagement.parameterSettings') }}</span>
            </div>
            <div class="connection-status" style="display: flex; align-items: center; margin: 10px 20px; padding: 10px; background-color: #f5f7fa; border-radius: 4px;">
              <el-tag size="large" :type="instrumentConnected ? 'success' : 'info'" :loading="instrumentConnectLoading">
                {{ instrumentConnected ? $t('ProductUpgrade.online') : $t('ProductUpgrade.offline') }}
              </el-tag>
              <el-button 
                @click="connectInstrument" 
                link 
                type="success" 
                style="margin-left: 10px;">
                {{ $t('ProductUpgrade.reconnecting') }}
              </el-button>
              <div v-if="instrumentInfo" style="margin-left: 20px;">
                <span v-if="instrumentInfo.serialNumber" style="margin-right: 15px;">{{ $t('ProductUpgrade.serialNumber') }}: {{ instrumentInfo.serialNumber }}</span>
                <span v-if="instrumentInfo.firmwareVersion">{{ $t('ProductUpgrade.firmwareVersion') }}: {{ instrumentInfo.firmwareVersion }}</span>
              </div>
            </div>
            <div class="parameter-content">
              <div class="no-params">
                <span>{{ $t('ParameterManagement.noInstrumentParams') }}</span>
              </div>
            </div>
          </div>
        </el-tab-pane>

      </el-tabs>
    </div>
  </div>
</template>

<script>
import { Edit } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import Mixin from '@/mixins/Mixin.js'
import i18n from '@/i18n'
import api from '@/views/ProductUpgrade/api'
import { saveLimitSpeedRecord } from '@/api/api.js'
const t = i18n.global.t
export default {
  name: 'ParameterManagement',
  components: {
    Edit
  },
  mixins: [Mixin],
  data() {
        return {
          activeTab: 'motor',
          isEditing: false,
          // 参数数组，方便后续动态添加更多参数
          parameters: [
            {
              key: 'speedLimit',
              value: 25
            }
          ],
          // 临时参数值，用于编辑状态
          tempParams: {
            speedLimit: 25
          },
          // 临时档位速度值，用于编辑状态
          gearTempSpeeds: {},
          // 存储档位最大速度信息
          gearMaxSpeeds: [],
          // 最大档位数量
          maxLevel: 0,
          // 档位速度折叠状态
          gearSpeedsCollapsed: false,
      // WebSocket相关状态
      ws: null,
      wsPath: '',
      // 电机状态
      motorConnected: false,
      motorConnectLoading: false,
      // 电机信息从computed中获取，这里不再定义
      // 仪表状态
      instrumentConnected: false,
      instrumentConnectLoading: false,
      instrumentInfo: {},

      // 速度限制范围
      minSpeed: 1,
      maxSpeed: 25
    }
  },
  computed: {
    // 从store获取电机信息
    motorInfo() {
      const info = this.$store.state.motorInfo;
      return info;
    }
  },
  mounted() {
    // 组件挂载时自动连接电机
    this.connectMotor()
  },
  beforeUnmount() {
    // 组件卸载时关闭WebSocket连接
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  },
  methods: {
    // 初始化WebSocket连接
    initWs() {
      return new Promise((resolve) => {
        if (this.ws !== null && this.ws.readyState === WebSocket.OPEN) {
          resolve()
          return
        }
        
        this.ws = new WebSocket('ws://127.0.0.1:1234')
        this.ws.onopen = () => {
      this.wsSend('getExeVer')
      // 获取硬件协议信息并发送updateCanList和updateUartList
      api.hardwareProtocol().then(res => {
        setTimeout(() => {
          this.wsSend('updateCanList', res.can)
        }, 200)
        setTimeout(() => {
          this.wsSend('updateUartList', res.uart)
          // 直接解析Promise，不再获取应用数据路径
          resolve()
        }, 400)
      }).catch(error => {
        console.error('获取硬件协议失败:', error)
        // 即使获取失败，也继续执行以不阻塞流程
        resolve()
      })
    }
        
        this.ws.onmessage = (message) => {
          this.handleWsMessage(message)
        }
        
        this.ws.onerror = () => {
          this.motorConnectLoading = false
          // 仪表连接状态
          this.instrumentConnectLoading = false
          ElMessage.error(t('ProductUpgrade.connectionFailure'))
        }
        
        this.ws.onclose = () => {
          this.motorConnected = false
          this.motorConnectLoading = false
          // 仪表连接状态
          this.instrumentConnected = false
          this.instrumentConnectLoading = false
        }
      })
    },
    
    // 发送WebSocket消息，返回Promise等待响应
    wsSend(cmd, param = '') {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        console.error('WebSocket not connected')
        return Promise.reject(new Error('WebSocket not connected'))
      }
      
      return new Promise((resolve, reject) => {
        // 保存当前的onmessage处理函数
        const originalOnMessage = this.ws.onmessage;
        
        // 临时修改onmessage处理函数，等待当前指令的响应
        this.ws.onmessage = (message) => {
          try {
            const messageData = JSON.parse(message.data);
            
            // 恢复原始的onmessage处理函数
            this.ws.onmessage = originalOnMessage;
            
            // 调用原始的消息处理逻辑
            this.handleWsMessage(message);
            
            // 如果是当前指令的响应，解析并resolve
            if (messageData.cmd === cmd) {
              resolve(messageData);
            } else {
              // 如果不是当前指令的响应，继续等待
              this.ws.onmessage = (newMessage) => {
                this.handleWsMessage(newMessage);
                const newMessageData = JSON.parse(newMessage.data);
                if (newMessageData.cmd === cmd) {
                  this.ws.onmessage = originalOnMessage;
                  resolve(newMessageData);
                }
              };
            }
          } catch (error) {
            this.ws.onmessage = originalOnMessage;
            reject(error);
          }
        };
        
        // 发送消息
        const data = typeof param === 'string' ? param : JSON.stringify(param);
        const message = {
          cmd: cmd,
          param: data
        };
        this.ws.send(JSON.stringify(message));
      });
    },
    
    // 处理档位最大速度信息
    processGearMaxSpeeds(configData) {
      // 从配置中获取最大档位数量
      this.maxLevel = parseInt(configData.maxLevel || 0);
      const gearMaxSpeeds = [];
      
      // 只遍历到maxLevel的档位数量
      for (let i = 1; i <= this.maxLevel; i++) {
        const speedKey = `bikeSpeed${i}`;
        if (Object.prototype.hasOwnProperty.call(configData, speedKey)) {
          const speedValue = parseFloat(configData[speedKey]);
          if (!isNaN(speedValue)) {
            gearMaxSpeeds.push({
              level: i,
              speed: speedValue
            });
            // 同时更新临时档位速度值，用于编辑状态
            this.gearTempSpeeds[`gear${i}`] = speedValue;
          }
        } else {
          // 如果没有对应的速度值，添加一个默认值或0
          gearMaxSpeeds.push({
            level: i,
            speed: 0
          });
          this.gearTempSpeeds[`gear${i}`] = 0;
        }
      }
      
      // 只显示maxLevel个档位
      this.gearMaxSpeeds = gearMaxSpeeds;
      
      // 使用bikeSpeed9作为限速设置的最大值
      if (Object.prototype.hasOwnProperty.call(configData, 'bikeSpeed9')) {
        const bikeSpeed9 = parseFloat(configData.bikeSpeed9);
        if (!isNaN(bikeSpeed9)) {
          this.maxSpeed = bikeSpeed9;
        }
      } else {
        // 如果没有bikeSpeed9，回退到计算所有档位速度的最大值
        if (gearMaxSpeeds.length > 0) {
          const maxGearSpeed = Math.max(...gearMaxSpeeds.map(g => g.speed));
          this.maxSpeed = maxGearSpeed;
        }
      }
      
      // 更新参数为实际读取的最大值
      this.parameters[0].value = this.maxSpeed;
      this.tempParams.speedLimit = this.maxSpeed;
    },
    
    // 处理WebSocket消息
    handleWsMessage(message) {
      try {
        const messageData = JSON.parse(message.data)
        
        // 添加对getMotorInfo命令的处理
        if (messageData.cmd === 'getMotorInfo') {
          if (messageData.code === 200) {
            try {
              // 解析data字段中的JSON字符串
              let motorData = {};
              if (messageData.data && typeof messageData.data === 'string') {
                motorData = JSON.parse(messageData.data);
                
                // 保存电机信息到组件的data中
                // 由于motorInfo是computed属性，我们需要通过Vue.set或直接提交到store
                this.$store.commit('setMotorInfo', motorData);
              }
            } catch (error) {
              console.error('Error parsing motor info data:', error)
            }
          }
        } else if (messageData.cmd === 'getMotorInfo_BikeSpeed') {
          // 修改handleGetMotorInfoMessage的功能到这里
          if (messageData.code === 200) {
            try {
                // 首先解析整个消息数据
                let configData = {};
                if (messageData.data && typeof messageData.data === 'string') {
                  // 从messageData.data中解析，因为data字段包含JSON字符串
                  configData = JSON.parse(messageData.data);
                } else if (messageData.data) {
                  configData = messageData.data;
                }
                
                // 设置电机连接状态为在线
                this.motorConnected = true;
                ElMessage.success(t('ProductUpgrade.connectionSuccess'));
                
                // 不再从configData中获取maxSpeed，改为从档位速度中计算最大值
                // 这个计算会在processGearMaxSpeeds方法中完成
                
                // 合并配置信息到现有的电机信息中
                const currentMotorInfo = this.$store.state.motorInfo || {};
                const mergedMotorInfo = {
                  ...currentMotorInfo,
                  ...configData
                };
                
                // 更新到store
                this.$store.commit('setMotorInfo', mergedMotorInfo);
                
                // 处理档位最大速度信息
                this.processGearMaxSpeeds(configData);
            } catch (error) {
              console.error('Error parsing motor config data:', error)
            }
          } else {
            ElMessage.error(t('ProductUpgrade.connectionFailed'))
            this.motorConnected = false
          }
          this.motorConnectLoading = false

        } else if (messageData.cmd === 'getDisplayInfo') {
          // 处理仪表信息
          if (messageData.code === 200) {
            try {
              const displayData = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
              this.instrumentInfo = displayData
              this.instrumentConnected = true
              ElMessage.success(t('ProductUpgrade.connectionSuccess'))
            } catch (error) {
              console.error('Error parsing display data:', error)
            }
          } else {
            ElMessage.error(t('ProductUpgrade.connectionFailed'))
            this.instrumentConnected = false
          }
          this.instrumentConnectLoading = false
        } else if (messageData.cmd === 'DeviceLose') {
          ElMessage.error(t('ProductUpgrade.connectionLost'))
          this.motorConnected = false
          // 仪表连接状态
          this.instrumentConnected = false
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    },
    

    

    
    // 发送connectMotor指令，返回Promise等待响应
    sendConnectMotor() {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        console.error('WebSocket not connected')
        return Promise.reject(new Error('WebSocket not connected'))
      }
      
      return new Promise((resolve, reject) => {
        // 保存当前的onmessage处理函数
        const originalOnMessage = this.ws.onmessage;
        
        // 临时修改onmessage处理函数，等待当前指令的响应
        this.ws.onmessage = (message) => {
          try {
            const messageData = JSON.parse(message.data);
            
            // 恢复原始的onmessage处理函数
            this.ws.onmessage = originalOnMessage;
            
            // 调用原始的消息处理逻辑
            this.handleWsMessage(message);
            
            // resolve响应
            resolve(messageData);
          } catch (error) {
            this.ws.onmessage = originalOnMessage;
            reject(error);
          }
        };
        
        // 发送消息
        this.ws.send(JSON.stringify({ cmd: 'connectMotor', timeout: '0' }));
      });
    },
    
    // 连接电机
    async connectMotor() {
      this.motorConnectLoading = true
      this.motorConnected = false
      
      try {
        await this.initWs()
        
        // 按顺序发送指令，等待每个指令的响应
        await this.wsSend('closePort');
        await this.sendConnectMotor();
        await this.connectMotorWs();
      } catch (error) {
        console.error('Error connecting to motor:', error)
        this.motorConnectLoading = false
      }
    },
    
    
    // 连接电机WebSocket - 按照正确顺序发送指令
    async connectMotorWs() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 按顺序发送指令，等待每个指令的响应
        await this.wsSend('getMotorInfo');
        await this.wsSend('getMotorInfo_BikeSpeed');
      } else {
        console.error(t('ProductUpgrade.connectionFailure'))
        this.motorConnectLoading = false
      }
    },

    // 连接仪表
    connectInstrument() {
      this.instrumentConnectLoading = true
      this.instrumentConnected = false
      
      try {
        this.initWs().then(() => {
          setTimeout(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
              // 获取仪表信息
              this.wsSend('getDisplayInfo')
            } else {
              console.error(t('ProductUpgrade.connectionFailed'))
              this.instrumentConnectLoading = false
            }
          }, 200)
        })
      } catch (error) {
        console.error('Error connecting to instrument:', error)
        this.instrumentConnectLoading = false
      }
    },

    // 开始编辑
    startEditing() {
      if (!this.motorConnected) {
        ElMessage.warning(t('ParameterManagement.connectMotorFirst'))
        return
      }
      
      // 复制当前参数值到临时参数对象
      this.parameters.forEach(param => {
        this.tempParams[param.key] = param.value
      })
      
      // 复制当前档位速度值到临时档位速度对象
      // 只复制从1到maxLevel的档位，添加maxLevel检查避免循环问题
      if (this.maxLevel > 0) {
        for (let i = 1; i <= this.maxLevel; i++) {
          const gearSpeed = this.gearMaxSpeeds.find(g => g.level === i)
          if (gearSpeed) {
            this.gearTempSpeeds['gear' + i] = gearSpeed.speed
          }
        }
      }
      
      this.isEditing = true
    },
    
    // 处理参数变化
    handleParameterChange(key, value) {
      // 更新临时参数值
      this.tempParams[key] = value
      
      // 如果修改的是限速参数，同时更新所有档位的临时速度值
      if (key === 'speedLimit' && this.isEditing) {
        // 只更新从1到maxLevel的档位
        for (let i = 1; i <= this.maxLevel; i++) {
          this.gearTempSpeeds['gear' + i] = value
        }
      }
    },
    
    // 获取档位的最小速度（动态依赖于上一个档位）
    getGearMinSpeed(level) {
      if (level > 1) {
        const prevSpeed = this.gearTempSpeeds['gear' + (level - 1)];
        return prevSpeed !== undefined ? prevSpeed : this.minSpeed;
      }
      return this.minSpeed;
    },
    
    // 处理档位速度变化
    handleGearSpeedChange(level, value) {
      // 确保值不为undefined或null
      if (value === undefined || value === null) {
        return;
      }
      
      // 确保速度不小于最小值和上一个档位的速度
      const minAllowedSpeed = this.getGearMinSpeed(level);
      if (value < minAllowedSpeed) {
        ElMessage.warning(this.$t('ParameterManagement.currentGearSpeedCannotLessThanPrevious'));
        // 强制设置为允许的最小速度
        value = minAllowedSpeed;
      }
      
      // 确保速度不大于最大值
      if (value > this.maxSpeed) {
        value = this.maxSpeed;
      }
      
      // 当修改当前档位时，更新后续档位的最小值（如果需要）
      if (level < this.maxLevel) {
        for (let i = level + 1; i <= this.maxLevel; i++) {
          const currentNextSpeed = this.gearTempSpeeds['gear' + i];
          if (currentNextSpeed !== undefined && currentNextSpeed < value) {
            // 后续档位速度不能小于当前档位，自动调整
            this.gearTempSpeeds['gear' + i] = value;
          }
        }
      }
      
      // 更新临时档位速度值
      this.gearTempSpeeds['gear' + level] = value;
    },
    
    // 保存更改
    async saveChanges() {
      try {
        if (!this.motorConnected) {
          ElMessage.error(t('ParameterManagement.connectMotorFirst'))
          return
        }
        
        // 验证所有档位速度是否满足递增关系
        let isValid = true;
        for (let i = 2; i <= this.maxLevel; i++) {
          const currentSpeed = this.gearTempSpeeds['gear' + i];
          const prevSpeed = this.gearTempSpeeds['gear' + (i - 1)];
          
          if (currentSpeed < prevSpeed) {
            isValid = false;
            ElMessage.error(`${this.$t('ParameterManagement.gear')} ${i} ${this.$t('ParameterManagement.speedCannotLessThanGear')} ${i - 1}`);
            break;
          }
        }
        
        if (!isValid) {
          return;
        }
        
        // 保存修改前的数据用于记录
        const oldGearSpeeds = []
        for (let i = 1; i <= this.maxLevel; i++) {
          const oldSpeed = this.gearMaxSpeeds.find(g => g.level === i)
          if (oldSpeed) {
            oldGearSpeeds.push(oldSpeed.speed)
          }
        }
        const oldData = oldGearSpeeds.join(';')
        
        // 构建分号分割的档位限速值字符串
        const gearSpeeds = []
        // 只处理从1到maxLevel的档位
        for (let i = 1; i <= this.maxLevel; i++) {
          const newSpeed = this.gearTempSpeeds['gear' + i]
          // 确保速度不小于最小值
          const speed = Math.max(newSpeed || 0, this.minSpeed)
          gearSpeeds.push(speed)
        }
        const speedsString = gearSpeeds.join(';')
        
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          // 发送update_param指令
          const updateCommand = {
            cmd: 'update_param',
            param_key: 'bike_speed',
            p1: speedsString,
            p2: ''
          }
          this.ws.send(JSON.stringify(updateCommand))
          
          // 调用后端接口记录限速修改
          // 从store中直接获取motorInfo，因为我们已经在handleWsMessage中更新了store
          const storeMotorInfo = this.$store.state.motorInfo;
          
          // 获取硬件和软件版本 - 从store的motorInfo中直接获取
          const hardwareVersion = storeMotorInfo?.hardwareversion || storeMotorInfo?.HardwareVersion || '';
          const softwareVersion = storeMotorInfo?.softwareversion || storeMotorInfo?.FirmwareVersion || storeMotorInfo?.firmwareversion || '';
          
          // 调用接口记录限速变更
          saveLimitSpeedRecord({
            old_data: oldData,
            new_data: speedsString,
            hardware_version: hardwareVersion,
            software_version: softwareVersion
          }).catch((error) => {
             console.error('调用限速记录接口失败:', error);
            // 不影响保存功能，只记录错误
          });
        }
        
        // 更新本地参数值
        this.parameters.forEach(param => {
          param.value = this.tempParams[param.key]
        })
        
        // 更新本地档位速度值
        this.gearMaxSpeeds.forEach(gearSpeed => {
          const level = gearSpeed.level
          const newSpeed = this.gearTempSpeeds['gear' + level]
          if (newSpeed !== undefined && newSpeed !== null) {
            gearSpeed.speed = newSpeed
          }
        })
        
        this.isEditing = false
        this.$message.success(this.$t('ParameterManagement.saveSuccess'))
      } catch (error) {
        this.$message.error(this.$t('ParameterManagement.saveFailed'))
        console.error('保存失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.parameter-management-container {
  padding: 20px;
  background-color: #fff;
  min-height: 100vh;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.all-tab {
  margin-left: 0;
}

.parameter-tabs {
    margin-top: 20px;
  }

  /* 档位速度折叠相关样式 */
  .gear-speeds-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background-color: #f5f7fa;
    border-bottom: 1px solid #ebeef5;
    font-weight: bold;
  }

  .collapse-btn {
    color: #409eff;
    font-size: 12px;
  }

  .gear-speeds-content {
    max-height: 500px;
    overflow-y: auto;
  }

  /* 参数框样式 */
.parameter-box {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
  margin: 0 20px;
}

.parameter-header {
  background-color: #f5f7fa;
  padding: 12px 20px;
  border-bottom: 1px solid #ebeef5;
}

.parameter-header span {
  font-size: 16px;
  color: #303133;
  font-weight: 500;
}

.parameter-content {
  padding: 0;
}

/* 参数项样式 */
.parameter-item {
  padding: 15px 20px;
  background-color: #fff;
}

/* 参数容器 */
  .parameter-container {
    width: 100%;
  }

  /* 参数行 */
  .parameter-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
  }
  
  /* 参数值容器位置 */
  .parameter-value {
    margin-left: 600px;
  }

  /* 参数标签 */
  .parameter-label {
    font-size: 22px;
    color: #606266;
    font-weight: normal;
  }

  /* 参数值 */
  .parameter-value {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-left: 600px;
  }

  .value-text {
    font-size: 22px;
    color: #303133;
    font-weight: normal;
  }

  /* 编辑图标 */
  .edit-icon {
    font-size: 20px;
    color: rgb(0, 154, 68) !important;;
    cursor: pointer;
    font-weight: normal;
  }

  /* 编辑控件 */
  .edit-controls {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-left: 600px;
  }

  .input-with-unit {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .unit {
    color: #606266;
    font-size: 22px;
    font-weight: normal;
  }

  .range-info {
    color: #909399;
    font-size: 16px;
  }

  /* 调整数字输入框的字体大小 */
  :deep(.el-input-number__input) {
    font-size: 22px !important;
    padding: 0 15px;
    height: 40px;
  }
  
  :deep(.el-input-number__decrease),
  :deep(.el-input-number__increase) {
    font-size: 22px;
    width: 40px;
    height: 40px;
  }

  /* 数字输入框样式 */
  .input-number {
    width: 120px;
  }

  /* 表单级操作按钮 */
  .form-actions {
    display: flex;
    justify-content: center;
    padding: 20px;
    background-color: #f5f7fa;
    border-top: 1px solid #ebeef5;
  }

  /* 保存按钮 */
  .save-btn {
    background-color: rgb(0, 154, 68) !important;; /* 确保为绿色 */
    color: white;
    padding: 8px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
    display: inline-block;
  }

  .save-btn:hover {
    background-color: #85ce61; /* 绿色悬停效果 */
  }

/* 参数分隔线 */
.parameter-divider {
  height: 1px;
  background-color: #f0f0f0;
  margin: 0;
}

/* 无参数提示样式 */
.no-params {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .parameter-management-container {
    padding: 10px;
  }
  
  .parameter-value-with-action {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .parameter-actions {
    width: 100%;
  }
  
  .parameter-divider {
    margin: 0 -20px;
    }
  }
</style>